import { useState } from 'react'
import {
  Search, History, AlertTriangle, Building2, User, ChevronDown, ChevronRight,
  FileWarning, ShieldCheck,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import ExportButtons from '../../components/ExportButtons'
import { localizeApiDetail } from '../../i18n/apiError'
import { formatNorwegianDate } from '../../utils/dateUtils'
import HeftelseKort from './HeftelseKort'
import KjoretoyDetaljer from './KjoretoyDetaljer'
import KvoteBadge from './KvoteBadge'
import {
  eierhistorikk,
  heftelseOppslag,
  kjoretoyOppslag,
  type EierhistorikkResponse,
  type Eier,
  type HeftelseTreff,
  type Kvote,
  type KjoretoyRad,
} from './oppslagApi'
import type { OppslagKey } from './detaljFelter'

const MAKS_STEG = 10

/** dd.MM.yyyy, as everywhere else in the app. */
const dato = (v: unknown) => (v ? formatNorwegianDate(String(v)) : '—')

/** One row per vehicle, owner flattened alongside it. The owner sits in a
 *  nested object in the API response, and an export of "[object Object]" is
 *  worse than no export. */
type Kolonne = { key: string; labelKey: OppslagKey; format?: (v: unknown) => string }

const KJORETOY_KOLONNER: Kolonne[] = [
  { key: 'kjennemerke', labelKey: 'kolonner.kjennemerke' },
  { key: 'merke', labelKey: 'kolonner.merke' },
  { key: 'handelsbetegnelse', labelKey: 'kolonner.modell' },
  { key: 'understellsnummer', labelKey: 'kolonner.understellsnummer' },
  { key: 'registrert_forstegang', labelKey: 'kolonner.regForsteGang', format: dato },
  { key: 'registreringsstatus_tekst', labelKey: 'kolonner.status' },
  { key: 'pkk_kontrollfrist', labelKey: 'kolonner.fristEuKontroll', format: dato },
  { key: 'pkk_sist_godkjent', labelKey: 'kolonner.sistEuGodkjent', format: dato },
  { key: 'eier_navn', labelKey: 'kolonner.eier' },
  { key: 'eier_type', labelKey: 'kolonner.eiertype' },
  { key: 'organisasjonsnummer', labelKey: 'kolonner.orgnr' },
  { key: 'adresse', labelKey: 'kolonner.adresse' },
  { key: 'registrert_pa_eier', labelKey: 'kolonner.registrertPaEier', format: dato },
  // A co-owner or lessee that shows on screen but not in the export would
  // make the export the more dangerous of the two documents.
  { key: 'medeier_navn', labelKey: 'kolonner.medeier' },
  { key: 'medeier_orgnr', labelKey: 'kolonner.medeierOrgnr' },
  { key: 'medeier_adresse', labelKey: 'kolonner.medeierAdresse' },
  { key: 'leasingtaker_navn', labelKey: 'kolonner.leasingtaker' },
  { key: 'leasingtaker_orgnr', labelKey: 'kolonner.leasingtakerOrgnr' },
  { key: 'leasingtaker_adresse', labelKey: 'kolonner.leasingtakerAdresse' },
]

const HISTORIKK_KOLONNER: Kolonne[] = [
  { key: 'steg', labelKey: 'kolonner.steg' },
  { key: 'eier_navn', labelKey: 'kolonner.eier' },
  { key: 'eier_type', labelKey: 'kolonner.eiertype' },
  { key: 'organisasjonsnummer', labelKey: 'kolonner.orgnr' },
  { key: 'adresse', labelKey: 'kolonner.adresse' },
  { key: 'registrert_pa_eier', labelKey: 'kolonner.registrertPaEier', format: dato },
  { key: 'medeier_navn', labelKey: 'kolonner.medeier' },
  { key: 'leasingtaker_navn', labelKey: 'kolonner.leasingtaker' },
]

function adresseAv(part: Eier | null | undefined) {
  return [part?.adresselinje1, part?.postnummer, part?.poststed]
    .filter(Boolean)
    .join(', ')
}

function eierFelter(
  eier: Eier | null | undefined,
  medeier?: Eier | null,
  leasingtaker?: Eier | null,
) {
  return {
    eier_navn: eier?.navn ?? '',
    eier_type: eier?.eier_type ?? '',
    organisasjonsnummer: eier?.organisasjonsnummer ?? '',
    adresse: adresseAv(eier),
    registrert_pa_eier: eier?.registrert_pa_eier
      ? formatNorwegianDate(eier.registrert_pa_eier)
      : '',
    medeier_navn: medeier?.navn ?? '',
    medeier_orgnr: medeier?.organisasjonsnummer ?? '',
    medeier_adresse: adresseAv(medeier),
    leasingtaker_navn: leasingtaker?.navn ?? '',
    leasingtaker_orgnr: leasingtaker?.organisasjonsnummer ?? '',
    leasingtaker_adresse: adresseAv(leasingtaker),
  }
}

/** Why the walk-back stopped. A chain cut short at maks_steg is not a complete
 *  history, and must not be presented as one. */
const STOPP_TEKST: Record<string, OppslagKey> = {
  ingen_flere_eiere: 'kjoretoy.stopp.ingenFlereEiere',
  maks_steg: 'kjoretoy.stopp.maksSteg',
  tidligst: 'kjoretoy.stopp.tidligst',
  feilmelding: 'kjoretoy.stopp.feilmelding',
  kvote: 'kjoretoy.stopp.kvote',
}

function EierKort({ eier }: { eier: Eier | null }) {
  const { t } = useTranslation('oppslag')
  if (!eier) {
    return <span className="text-gray-500 dark:text-gray-400">{t('kjoretoy.noOwner')}</span>
  }
  const Ikon = eier.eier_type === 'organisasjon' ? Building2 : User
  return (
    <div className="flex items-start gap-2">
      <Ikon className="w-4 h-4 mt-0.5 text-gray-400 shrink-0" />
      <div>
        <div className="font-medium text-gray-900 dark:text-white">
          {eier.navn ?? '—'}
          {eier.organisasjonsnummer && (
            <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
              {t('felles.orgnr', { orgnr: eier.organisasjonsnummer })}
            </span>
          )}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {[eier.adresselinje1, eier.postnummer, eier.poststed]
            .filter(Boolean)
            .join(', ') || '—'}
        </div>
      </div>
    </div>
  )
}

/**
 * A party banner: owner, co-owner or lessee.
 *
 * Co-owner and lessee get their own colour and a one-line consequence,
 * because both change what a dealer has to do and neither is visible from
 * the owner field alone. A co-owner must co-sign the sale; a lessee means
 * the person driving the car is not the person who can sell it. Rendering
 * them as another grey line in the owner block is how they get missed.
 */
function PartBanner({
  rolle,
  part,
  farge,
  konsekvens,
  testid,
}: {
  rolle: string
  part: Eier
  farge: 'amber' | 'indigo'
  konsekvens: string
  testid: string
}) {
  const { t } = useTranslation('oppslag')
  const stiler = {
    amber:
      'border-amber-400 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-600',
    indigo:
      'border-indigo-400 bg-indigo-50 dark:bg-indigo-950/30 dark:border-indigo-600',
  }[farge]
  const merkefarge = {
    amber: 'text-amber-800 dark:text-amber-300',
    indigo: 'text-indigo-800 dark:text-indigo-300',
  }[farge]

  return (
    <div className={`border-l-4 rounded-sm p-3 ${stiler}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle className={`w-4 h-4 ${merkefarge}`} />
        <span className={`text-xs font-bold uppercase tracking-wide ${merkefarge}`}>
          {rolle}
        </span>
        {part.registrert_pa_eier && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {t('kjoretoy.since', { dato: dato(part.registrert_pa_eier) })}
          </span>
        )}
      </div>
      <EierKort eier={part} />
      <p className={`text-xs mt-1 ${merkefarge}`}>{konsekvens}</p>
    </div>
  )
}

/** Days until the EU-kontroll deadline, or null when there is no date.
 *  Takes the raw ISO value, not the formatted one — the parameter is named
 *  `iso` rather than `dato` so it cannot be confused with the formatter above. */
function dagerTil(iso: string | null | undefined): number | null {
  if (!iso) return null
  const frist = new Date(iso).getTime()
  if (Number.isNaN(frist)) return null
  return Math.ceil((frist - Date.now()) / 86_400_000)
}

export default function KjoretoyTab() {
  const { t } = useTranslation('oppslag')
  const [sok, setSok] = useState('')
  const [laster, setLaster] = useState(false)
  const [rad, setRad] = useState<KjoretoyRad | null>(null)
  const [historikk, setHistorikk] = useState<EierhistorikkResponse | null>(null)
  const [kvote, setKvote] = useState<Kvote | null>(null)
  const [feil, setFeil] = useState<string | null>(null)
  const [viserDetaljer, setViserDetaljer] = useState(false)
  const [heftelser, setHeftelser] = useState<HeftelseTreff | null>(null)
  const [henterHeftelser, setHenterHeftelser] = useState(false)
  const [inkluderHeftelser, setInkluderHeftelser] = useState(false)

  const dager = dagerTil(rad?.pkk_kontrollfrist)

  /**
   * Liens for the plate that was typed.
   *
   * Never automatic. Brreg is a separate agreement with its own daily budget,
   * so fetching liens with every vehicle lookup would spend a second quota on
   * every search whether or not the user cares. Either the user presses the
   * button, or they tick the box that says what it costs.
   *
   * Asks about the typed value rather than the plate SVV resolved. Where the
   * two differ the typed one may well miss, but the lookup costs a unit
   * either way, and answering the question that was actually asked beats
   * quietly substituting a different one.
   */
  async function hentHeftelser() {
    const verdi = sok.trim()
    if (!verdi) return
    setHenterHeftelser(true)
    setFeil(null)
    try {
      const svar = await heftelseOppslag([verdi])
      setHeftelser(svar.treff[0] ?? null)
      setKvote(svar.kvote)
      if (svar.feil.length) setFeil(svar.feil[0].kode)
    } catch (e) {
      visFeil(e)
    } finally {
      setHenterHeftelser(false)
    }
  }

  function visFeil(e: any) {
    const d = e?.response?.data?.detail
    setFeil(
      typeof d === 'string'
        ? (localizeApiDetail(d) ?? t('kjoretoy.lookupFailed'))
        : (localizeApiDetail(d?.melding) ?? t('kjoretoy.lookupFailed')),
    )
  }

  async function sokKjoretoy() {
    const verdi = sok.trim()
    if (!verdi) return
    setLaster(true)
    setFeil(null)
    setHistorikk(null)
    // Liens belong to the plate they were fetched for. Leaving them on
    // screen across a new search would attribute one car's debt to another.
    setHeftelser(null)
    try {
      const svar = await kjoretoyOppslag('kjennemerker', [verdi])
      setKvote(svar.kvote)
      const treff = svar.treff[0] ?? null
      setRad(treff)
      if (!treff) {
        setFeil(svar.feil[0]?.kode ?? t('feilkoder.ikkeFunnet'))
      } else if (inkluderHeftelser) {
        // Only once SVV found the car: a plate SVV does not recognise is
        // usually a typo, and it is not worth a Brreg unit.
        await hentHeftelser()
      }
    } catch (e) {
      visFeil(e)
      setRad(null)
    } finally {
      setLaster(false)
    }
  }

  async function sokHistorikk() {
    const verdi = sok.trim()
    if (!verdi) return
    setLaster(true)
    setFeil(null)
    // Same reason as in sokKjoretoy: liens belong to the plate they were
    // fetched for, and carrying them into another car's result attributes one
    // car's debt to another.
    setHeftelser(null)
    try {
      const svar = await eierhistorikk(verdi, MAKS_STEG)
      setHistorikk(svar)
      setKvote(svar.kvote)
      if (svar.kjoretoy) setRad(svar.kjoretoy)
      // The checkbox deliberately does NOT apply here. Løsøreregisteret
      // answers "what rests on this car now" and has no history, so showing
      // today's liens beside an owner from 2020 would invite exactly the
      // wrong inference — that this is what the car was encumbered with when
      // that owner held it.
    } catch (e) {
      visFeil(e)
    } finally {
      setLaster(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="card p-4 space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-semibold text-gray-900 dark:text-white">
              {t('kjoretoy.title')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('kjoretoy.intro')}
            </p>
          </div>
          <KvoteBadge kvote={kvote} api="maskinporten" />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <input
            data-testid="kjoretoy-input"
            value={sok}
            onChange={(e) => setSok(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sokKjoretoy()}
            placeholder="EE16703"
            className="rounded-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm font-mono w-48"
          />
          <button
            data-testid="kjoretoy-sok"
            onClick={sokKjoretoy}
            disabled={laster || !sok.trim()}
            className="btn-primary text-sm disabled:opacity-40 flex items-center gap-2"
          >
            <Search className="w-4 h-4" />
            {t('kjoretoy.search')}
          </button>
          <button
            data-testid="kjoretoy-historikk"
            onClick={sokHistorikk}
            disabled={laster || !sok.trim()}
            className="btn-secondary text-sm disabled:opacity-40 flex items-center gap-2"
            title={t('kjoretoy.historikkTitle', { n: MAKS_STEG })}
          >
            <History className="w-4 h-4" />
            {t('kjoretoy.historikk', { n: MAKS_STEG })}
          </button>
          <button
            data-testid="kjoretoy-heftelser"
            onClick={hentHeftelser}
            disabled={henterHeftelser || !sok.trim()}
            className="btn-secondary text-sm disabled:opacity-40 flex items-center gap-2"
            title={t('kjoretoy.heftelserTitle')}
          >
            <FileWarning className="w-4 h-4" />
            {henterHeftelser ? t('kjoretoy.fetching') : t('kjoretoy.heftelser')}
          </button>
        </div>

        {/* One search, the whole picture — but the second budget is never
            spent without the user saying so, and the label says what it
            costs before the click rather than after. */}
        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer w-fit">
          <input
            type="checkbox"
            data-testid="kjoretoy-inkluder-heftelser"
            checked={inkluderHeftelser}
            onChange={(e) => setInkluderHeftelser(e.target.checked)}
            className="rounded-sm border-gray-300 dark:border-gray-600"
          />
          {t('kjoretoy.inkluderHeftelser')}
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {t('kjoretoy.inkluderHeftelserCost')}
          </span>
        </label>
      </div>

      {feil && (
        <div
          data-testid="kjoretoy-feil"
          className="card p-3 flex items-start gap-2 text-sm text-red-700 dark:text-red-300"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{feil}</span>
        </div>
      )}

      {rad && (
        <div className="card p-4 space-y-3" data-testid="kjoretoy-kort">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div className="flex items-baseline gap-3 flex-wrap">
              <span className="text-lg font-bold text-gray-900 dark:text-white">
                {rad.kjennemerke}
              </span>
              <span className="text-gray-600 dark:text-gray-300">
                {rad.merke} {rad.handelsbetegnelse}
              </span>
              {rad.matchet_pa && (
                <span className="text-xs text-amber-700 dark:text-amber-300">
                  {t('kjoretoy.searchedFor', { verdi: rad.matchet_pa })}
                </span>
              )}
            </div>
            <ExportButtons
              data={[{
                ...rad,
                ...eierFelter(rad.eier, rad.medeier, rad.leasingtaker),
              }]}
              columns={KJORETOY_KOLONNER.map(({ labelKey, ...k }) => ({ ...k, label: t(labelKey) }))}
              metadata={{
                title: t('kjoretoy.exportTitle', { plate: rad.kjennemerke ?? '' }).trim(),
                explanationText: t('kjoretoy.exportExplanation'),
              }}
            />
          </div>

          <dl className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            {[
              [t('kolonner.understellsnummer'), rad.understellsnummer],
              [t('kolonner.regForsteGang'), dato(rad.registrert_forstegang)],
              [t('kolonner.status'), rad.registreringsstatus_tekst],
              [t('kolonner.sistEuGodkjent'), dato(rad.pkk_sist_godkjent)],
            ].map(([label, verdi]) => (
              <div key={label as string}>
                <dt className="text-xs text-gray-500 dark:text-gray-400">{label}</dt>
                <dd className="text-gray-900 dark:text-gray-100">{verdi ?? '—'}</dd>
              </div>
            ))}
            <div data-testid="kjoretoy-pkk">
              <dt className="text-xs text-gray-500 dark:text-gray-400">
                {t('kolonner.fristEuKontroll')}
              </dt>
              <dd
                className={
                  // Overdue or nearly due is the single fact most likely to
                  // change what the dealer does next, so it does not read as
                  // just another date in the row.
                  dager !== null && dager < 0
                    ? 'font-semibold text-red-600 dark:text-red-400'
                    : dager !== null && dager <= 60
                      ? 'font-semibold text-amber-700 dark:text-amber-400'
                      : 'text-gray-900 dark:text-gray-100'
                }
              >
                {dato(rad.pkk_kontrollfrist)}
                {dager !== null && (
                  <span className="block text-xs font-normal">
                    {dager < 0
                      ? t('kjoretoy.overdue', { n: Math.abs(dager) })
                      : t('kjoretoy.inDays', { n: dager })}
                  </span>
                )}
              </dd>
            </div>
          </dl>

          <div className="pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {t('kjoretoy.registeredOwner')}
              {rad.eier?.registrert_pa_eier &&
                t('kjoretoy.sinceDash', { dato: dato(rad.eier.registrert_pa_eier) })}
            </div>
            <EierKort eier={rad.eier ?? null} />

            {rad.medeier && (
              <PartBanner
                rolle={t('kolonner.medeier')}
                part={rad.medeier}
                farge="amber"
                konsekvens={t('kjoretoy.medeierConsequence')}
                testid="kjoretoy-medeier"
              />
            )}

            {rad.leasingtaker && (
              <PartBanner
                rolle={t('kolonner.leasingtaker')}
                part={rad.leasingtaker}
                farge="indigo"
                konsekvens={
                  rad.leasingtaker.eier_type === 'organisasjon'
                    ? t('kjoretoy.naeringsleasing')
                    : t('kjoretoy.privatleasing')
                }
                testid="kjoretoy-leasingtaker"
              />
            )}
          </div>

          {heftelser && (
            <div
              className="pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2"
              data-testid="kjoretoy-heftelser-blokk"
            >
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {t('heftelser.title')}
                {heftelser.oppslagstidspunkt &&
                  t('kjoretoy.heftelserAsOf', { dato: dato(heftelser.oppslagstidspunkt) })}
              </div>
              {heftelser.heftelser.length === 0 ? (
                <div
                  className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400"
                  data-testid="kjoretoy-ingen-heftelser"
                >
                  <ShieldCheck className="w-4 h-4" />
                  {t('heftelser.noneRegistered')}
                </div>
              ) : (
                heftelser.heftelser.map((h, i) => (
                  <HeftelseKort key={h.dokumentnummer ?? i} heftelse={h} />
                ))
              )}
            </div>
          )}

          {rad.detaljer && (
            <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
              <button
                data-testid="kjoretoy-detaljer-knapp"
                onClick={() => setViserDetaljer((v) => !v)}
                aria-expanded={viserDetaljer}
                className="inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                {viserDetaljer ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
                {viserDetaljer ? t('kjoretoy.hideDetails') : t('kjoretoy.showDetails')}
              </button>
              {viserDetaljer && (
                <div className="mt-2">
                  <KjoretoyDetaljer
                    detaljer={rad.detaljer}
                    kjennemerke={rad.kjennemerke}
                    beskrivelse={[rad.merke, rad.handelsbetegnelse]
                      .filter(Boolean)
                      .join(' ')}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {historikk && (
        <div className="card p-4 space-y-3" data-testid="kjoretoy-historikk-liste">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {t('kjoretoy.historikkHeading', { n: historikk.historikk.length })}
            </h3>
            <ExportButtons
              data={historikk.historikk.map((ledd) => ({
                steg: ledd.steg === 0 ? t('kjoretoy.now') : ledd.steg,
                ...eierFelter(ledd.eier, ledd.medeier, ledd.leasingtaker),
              }))}
              columns={HISTORIKK_KOLONNER.map(({ labelKey, ...k }) => ({ ...k, label: t(labelKey) }))}
              metadata={{
                title: t('kjoretoy.historikkExportTitle', { plate: historikk.kjennemerke ?? '' }).trim(),
                // The reason the chain ended travels with the export. A
                // spreadsheet cut short at maks_steg looks complete otherwise.
                filters: [
                  STOPP_TEKST[historikk.stoppet_fordi]
                    ? t(STOPP_TEKST[historikk.stoppet_fordi])
                    : historikk.stoppet_fordi,
                ],
              }}
            />
          </div>

          <ol className="space-y-3">
            {historikk.historikk.map((ledd) => (
              <li
                key={ledd.steg}
                className="flex gap-3 pb-3 border-b border-gray-100 dark:border-gray-800 last:border-0"
              >
                <span className="text-xs text-gray-400 w-6 shrink-0 pt-0.5">
                  {ledd.steg === 0 ? t('kjoretoy.now') : ledd.steg}
                </span>
                <div className="flex-1">
                  <EierKort eier={ledd.eier} />
                  {ledd.registrert_pa_eier && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {t('kjoretoy.registeredOnOwner', { dato: dato(ledd.registrert_pa_eier) })}
                    </div>
                  )}
                  {/* Co-ownership and leasing at THAT moment, not today's. */}
                  {ledd.medeier && (
                    <div
                      className="text-xs text-amber-700 dark:text-amber-300 mt-1"
                      data-testid={`historikk-medeier-${ledd.steg}`}
                    >
                      {t('kjoretoy.medeierLine', { navn: ledd.medeier.navn ?? '—' })}
                    </div>
                  )}
                  {ledd.leasingtaker && (
                    <div
                      className="text-xs text-indigo-700 dark:text-indigo-300 mt-1"
                      data-testid={`historikk-leasingtaker-${ledd.steg}`}
                    >
                      {t('kjoretoy.leasingtakerLine', { navn: ledd.leasingtaker.navn ?? '—' })}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>

          <p
            data-testid="historikk-stopp"
            className={`text-xs ${
              historikk.stoppet_fordi === 'ingen_flere_eiere'
                ? 'text-gray-500 dark:text-gray-400'
                : 'text-amber-700 dark:text-amber-300'
            }`}
          >
            {STOPP_TEKST[historikk.stoppet_fordi]
              ? t(STOPP_TEKST[historikk.stoppet_fordi])
              : historikk.stoppet_fordi}
          </p>
        </div>
      )}
    </div>
  )
}
